// Turns a conversation into the plain data the printed record renders
// (PrintableConversation). Kept free of React so the print layout never
// depends on the on-screen chat bubbles.
import moment from 'moment';
import formatPhoneForUser from '../../../../utils/formatPhoneForUser.js';

const IMAGE_LOAD_TIMEOUT_MS = 10000;

const CHANNEL_LABELS = {
  phone: 'SMS',
  email: 'Email',
  webchat: 'Website widget',
  form: 'Form',
  facebook: 'Facebook Messenger',
  instagram: 'Instagram',
  google: 'Google',
};

const FILE_TYPES = { '.pdf': 'PDF', '.txt': 'TXT', '.csv': 'CSV' };
const VIDEO_EXTENSIONS = ['.mov', '.mp4'];
const AUDIO_EXTENSIONS = ['.mp3', '.m4a', '.amr'];

// Same detection the chat bubble uses to pick a file card, video or image.
const getMedia = (url) => {
  if (!url) return null;

  const fileExtension = Object.keys(FILE_TYPES).find((extension) => url.includes(extension));
  if (fileExtension) {
    return { kind: 'file', label: `${FILE_TYPES[fileExtension]} attachment`, url };
  }
  if (VIDEO_EXTENSIONS.some((extension) => url.includes(extension))) {
    return { kind: 'file', label: 'Video attachment', url };
  }
  if (AUDIO_EXTENSIONS.some((extension) => url.includes(extension))) {
    return { kind: 'file', label: 'Audio attachment', url };
  }
  return { kind: 'image', url };
};

const formatDuration = (seconds) => {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

const getUserName = (users, _user) => {
  const user = users.find((u) => u._id === (_user?._id || _user));
  return user ? user.name || user.username || '' : '';
};

// Mirrors the titles CallCard shows in the thread, without "You".
const describeCall = ({ call, users, twilioNumber }) => {
  const status = call.callStatus?.toLowerCase();
  const agentName = getUserName(users, call._user);
  const isVoicemail =
    status === 'voicemail' ||
    (call.direction === 'inbound' &&
      status === 'no-answer' &&
      Boolean(call.recording?.url) &&
      Boolean(call.transcription?.text));

  let title;
  if (call.direction === 'outbound') {
    if (status === 'no-answer') title = 'Outbound call, no answer';
    else if (status === 'busy') title = 'Outbound call, line busy';
    else if (status === 'failed') title = 'Outbound call failed';
    else title = agentName ? `Outbound call from ${agentName}` : 'Outbound call';
  } else if (isVoicemail) {
    title = 'Voicemail';
  } else if (status === 'no-answer') {
    title = 'Missed call';
  } else if (status === 'busy') {
    title = 'Inbound call, line busy';
  } else if (status === 'failed') {
    title = 'Inbound call failed';
  } else if (status === 'completed' && !call._user) {
    title =
      call.to && call.to !== twilioNumber
        ? `Inbound call forwarded to ${formatPhoneForUser(call.to)}`
        : 'Missed call';
  } else if (status === 'completed') {
    title = agentName ? `Inbound call answered by ${agentName}` : 'Inbound call';
  } else {
    title = 'Inbound call';
  }

  const duration = formatDuration(isVoicemail ? call.recording?.duration : call.duration);
  return duration ? `${title} (${duration})` : title;
};

const formatConversationPrint = ({
  activeConversation,
  users,
  messageAutomations,
  businessName,
  twilioNumber,
}) => {
  const safeUsers = Array.isArray(users) ? users : [];
  const safeAutomations = Array.isArray(messageAutomations) ? messageAutomations : [];
  const customers = (
    activeConversation.isGroup
      ? activeConversation._customers || []
      : [activeConversation._customer]
  ).filter(Boolean);

  const getCustomerName = (message) => {
    if (!activeConversation.isGroup) {
      const customer = customers[0];
      return customer?.name || formatPhoneForUser(customer?.phone) || 'Customer';
    }
    const senderId = String(message._customer?._id || message._customer || '');
    const participant = customers.find((c) => String(c?._id || c) === senderId);
    return participant?.name || formatPhoneForUser(participant?.phone) || 'Customer';
  };

  const getAuthor = (message) => {
    if (message.sender === 'customer') {
      return { author: getCustomerName(message), role: 'Customer' };
    }
    if (message.sender === 'ai') {
      return { author: 'AI Employee', role: businessName };
    }
    if (message.sender === 'note') {
      return {
        author: getUserName(safeUsers, message._user) || 'Team member',
        role: 'Internal note',
      };
    }
    if (message._messageAutomation) {
      const automation = safeAutomations.find((ma) => ma._id === message._messageAutomation);
      return { author: automation?.title || 'Message Automation', role: 'Automated message' };
    }
    const userName = getUserName(safeUsers, message._user);
    return userName
      ? { author: userName, role: businessName }
      : { author: businessName || 'Business', role: '' };
  };

  const sortedMessages = (activeConversation.messages || [])
    .filter(
      (message) =>
        message &&
        message._id !== 'no_message' &&
        message.active !== false &&
        // optimistic bubbles that never reached the customer
        message.status !== 'sending' &&
        message.status !== 'failed',
    )
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const messages = [];
  sortedMessages.forEach((message, index) => {
    const isEvent = message.sender === 'event';
    let text = message.text || '';

    if (isEvent && message._call) {
      text = describeCall({ call: message._call, users: safeUsers, twilioNumber });
    } else if (text.includes('@customer-response')) {
      // The form submission bubble right before this event already records it.
      const previous = sortedMessages[index - 1];
      if (previous?.sender === 'customer' && previous.channel === 'form') return;
      text = 'Questionnaire answers submitted';
    }

    // A note's photos and files are private links, so the record names them.
    const noteFiles = message.sender === 'note' ? message.attachments || [] : [];
    if (noteFiles.length) {
      const names = noteFiles.map((attachment) => attachment.name || 'Attachment').join(', ');
      text = [text, `Attached: ${names}`].filter(Boolean).join('\n');
    }

    const media = isEvent ? null : getMedia(message.media);
    if (!text && !media) return;

    const details = [];
    if (!isEvent && message.sender !== 'note' && message.channel) {
      details.push(CHANNEL_LABELS[message.channel] || message.channel);
    }
    if (message.SmsStatus) {
      details.push(message.SmsStatus);
    }

    messages.push({
      key: message._id || `${message.createdAt}-${index}`,
      sender: message.sender,
      ...(isEvent ? { author: '', role: '' } : getAuthor(message)),
      sentAt: moment(message.createdAt).format('LLL'),
      details: details.join(' · '),
      text,
      media,
    });
  });

  const first = sortedMessages[0];
  const last = sortedMessages[sortedMessages.length - 1];

  return {
    title: activeConversation.title || customers[0]?.name || 'Conversation',
    businessName: businessName || '',
    customers: customers.map((customer) => ({
      key: customer._id || customer.phone || customer.name,
      name: customer.name || '',
      phone: customer.phone ? formatPhoneForUser(customer.phone) : '',
      email: customer.email || '',
    })),
    period:
      first && last
        ? `${moment(first.createdAt).format('LL')} to ${moment(last.createdAt).format('LL')}`
        : '',
    messages,
  };
};

const loadImage = (url) =>
  new Promise((resolve) => {
    const image = new Image();
    const timer = setTimeout(() => resolve(false), IMAGE_LOAD_TIMEOUT_MS);
    const finish = (loaded) => {
      clearTimeout(timer);
      resolve(loaded);
    };
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = url;
  });

// Expired or slow images would otherwise print as a broken-image icon, so the
// record falls back to an attachment line for anything that will not load.
export const withLoadedImages = async (record) => {
  const urls = [
    ...new Set(record.messages.filter((m) => m.media?.kind === 'image').map((m) => m.media.url)),
  ];
  const loaded = await Promise.all(urls.map(loadImage));
  const unavailable = new Set(urls.filter((_, index) => !loaded[index]));

  if (!unavailable.size) return record;

  return {
    ...record,
    messages: record.messages.map((message) =>
      message.media?.kind === 'image' && unavailable.has(message.media.url)
        ? { ...message, media: { ...message.media, kind: 'file', label: 'Image attachment' } }
        : message,
    ),
  };
};

export default formatConversationPrint;
